/**
 * SnakeWidget — Snake als geheime widget. Score-zone + game-zone met outline (zoals
 * de Klok); het bord schaalt mee met de widget (cellen berekend uit de beschikbare
 * vierkante ruimte). Toetsen op het bord zelf (focusbaar) zodat het dashboard niet
 * scrollt. Geen positie-transitie meer → segmenten "sliden" niet meer over de randen
 * bij het doorlopen (wrap). Verbergen → TodayCard via SecretGameWidget.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { T } from './widgetTokens';
import SecretGameWidget from './SecretGameWidget';
import { GameFrame, useFitSquare } from './gameKit';

const GRID = 15;
const GAP = 2;
const SPEED_START = 150;
const SPEED_MIN = 65;
const DIR = { UP: [0, -1], DOWN: [0, 1], LEFT: [-1, 0], RIGHT: [1, 0] };
const INIT = [[7, 7], [6, 7], [5, 7]];

const randomPos = (snake) => {
  const taken = new Set(snake.map(([x, y]) => x * GRID + y));
  let idx;
  do { idx = Math.floor(Math.random() * GRID * GRID); } while (taken.has(idx));
  return [idx % GRID, Math.floor(idx / GRID)];
};

const LADYBUG = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><ellipse cx="12" cy="14" rx="8" ry="9" fill="#ef4444"/><line x1="12" y1="5" x2="12" y2="23" stroke="#1a1a1a" stroke-width="1.2"/><circle cx="8" cy="11" r="1.3" fill="#1a1a1a"/><circle cx="16" cy="11" r="1.3" fill="#1a1a1a"/><circle cx="11" cy="16" r="1.1" fill="#1a1a1a"/><circle cx="13" cy="16" r="1.1" fill="#1a1a1a"/><circle cx="12" cy="6" r="4" fill="#1a1a1a"/></svg>')}`;

const StatNum = ({ v, l, c }) => (
  <Box sx={{ textAlign: 'center', minWidth: 44 }}>
    <Typography sx={{ fontFamily: T.font, fontSize: '1.15rem', fontWeight: 800, color: c, lineHeight: 1 }}>{v}</Typography>
    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', color: T.outline }}>{l}</Typography>
  </Box>
);

function SnakeBoard() {
  const [snake, setSnake] = useState(INIT);
  const [food, setFood] = useState(() => randomPos(INIT));
  const [running, setRunning] = useState(false);
  const [dead, setDead] = useState(false);
  const [score, setScore] = useState(0);
  const dirRef = useRef(DIR.RIGHT);
  const lastDirRef = useRef(DIR.RIGHT);
  const boardRef = useRef(null);
  const [fitRef, size] = useFitSquare();

  const reset = useCallback(() => {
    setSnake(INIT); setFood(randomPos(INIT));
    dirRef.current = DIR.RIGHT; lastDirRef.current = DIR.RIGHT;
    setRunning(false); setDead(false); setScore(0);
  }, []);

  const onKeyDown = (e) => {
    const k = e.key.toLowerCase();
    let nd;
    if (k === 'arrowup' || k === 'w') nd = DIR.UP;
    else if (k === 'arrowdown' || k === 's') nd = DIR.DOWN;
    else if (k === 'arrowleft' || k === 'a') nd = DIR.LEFT;
    else if (k === 'arrowright' || k === 'd') nd = DIR.RIGHT;
    else if (k === ' ' && dead) { e.preventDefault(); reset(); return; }
    else return;
    e.preventDefault();
    const last = lastDirRef.current;
    if (nd[0] + last[0] === 0 && nd[1] + last[1] === 0) return;
    dirRef.current = nd;
    if (!running && !dead) setRunning(true);
  };

  const start = () => {
    if (dead) reset();
    setRunning(true);
    boardRef.current?.focus();
  };

  useEffect(() => {
    if (!running || dead) return undefined;
    const speed = Math.max(SPEED_MIN, SPEED_START - score * 3);
    const id = setInterval(() => {
      setSnake((prev) => {
        const d = dirRef.current;
        lastDirRef.current = d;
        const head = [(prev[0][0] + d[0] + GRID) % GRID, (prev[0][1] + d[1] + GRID) % GRID];
        if (prev.some(([x, y]) => x === head[0] && y === head[1])) {
          setDead(true); setRunning(false);
          return prev;
        }
        const next = [head, ...prev];
        if (head[0] === food[0] && head[1] === food[1]) { setScore((s) => s + 1); setFood(randomPos(next)); }
        else next.pop();
        return next;
      });
    }, speed);
    return () => clearInterval(id);
  }, [running, dead, food, score]);

  const cell = size > 8 ? Math.floor((size - GAP * (GRID + 1)) / GRID) : 0;
  const boardPx = cell > 0 ? cell * GRID + GAP * (GRID + 1) : 0;
  const at = (v) => GAP + v * (cell + GAP);

  const scoreZone = (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3.5 }}>
      <StatNum v={score} l="Score" c={T.emerald} />
      <StatNum v={snake.length} l="Lengte" c={T.outline} />
    </Box>
  );

  return (
    <GameFrame score={scoreZone}>
      <Box ref={fitRef} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {cell > 0 && (
          <Box
            ref={boardRef}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onClick={start}
            sx={{
              position: 'relative', width: boardPx, height: boardPx, outline: 'none',
              bgcolor: '#0f172a', borderRadius: '0.5rem', overflow: 'hidden', cursor: 'pointer',
              '&:focus-visible': { boxShadow: `0 0 0 2px ${T.primary}` },
            }}
          >
            {Array.from({ length: GRID * GRID }).map((_, i) => (
              <Box key={i} sx={{ position: 'absolute', left: at(i % GRID), top: at(Math.floor(i / GRID)), width: cell, height: cell, borderRadius: '2px', bgcolor: '#1e293b' }} />
            ))}
            {snake.map(([x, y], i) => (
              <Box key={`s${i}`} sx={{
                position: 'absolute', left: at(x), top: at(y), width: cell, height: cell,
                borderRadius: i === 0 ? '3px' : '2px', bgcolor: i === 0 ? '#4ade80' : '#22c55e',
                boxShadow: i === 0 ? '0 0 6px #4ade8066' : 'none', zIndex: 2,
              }} />
            ))}
            <Box sx={{
              position: 'absolute', left: at(food[0]) - 1, top: at(food[1]) - 1, width: cell + 2, height: cell + 2,
              zIndex: 3, backgroundImage: `url("${LADYBUG}")`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat',
              filter: 'drop-shadow(0 0 3px #ef444466)',
            }} />
            {(!running || dead) && (
              <Box sx={{
                position: 'absolute', inset: 0, bgcolor: '#0f172acc', zIndex: 10,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: '#fff',
              }}>
                {dead && <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1rem' }}>Game over · {score}</Typography>}
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', opacity: 0.85, textAlign: 'center', px: 2 }}>
                  {dead ? 'Klik om opnieuw te spelen' : 'Klik en gebruik de pijltjes / WASD'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </GameFrame>
  );
}

export default function SnakeWidget({ settings, onSettingsChange, widget }) {
  return (
    <SecretGameWidget settings={settings} onSettingsChange={onSettingsChange} widget={widget}>
      <SnakeBoard />
    </SecretGameWidget>
  );
}
